import { FC, ReactNode, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form-latest';
import {
  CHIP_VARIANT_BY_TAX_EXEMPTION_ANALYSIS_STATUS,
  COLOR_BY_STATUS,
  COMPANY_EXPENSE_ID,
  ClaimStatus,
  useClaim,
  useUpdateClaim,
} from 'store/claims';
import {
  PageHeaderLayout,
  Footer,
  NothingFoundAlert,
  Typography,
  Panel,
  ImageZoom,
  Loader,
  Button,
  ExternalLink,
  Link,
  LabeledValue,
  Chip,
  Alert,
  DateInput,
  NumberInput,
  TextInput,
} from 'melp-design/components';
import {
  Box,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import { ROUTES } from 'config';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { fromCents, toCents } from 'utils/general';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { useModalContext } from 'store/modal';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { usePredefinedToasts } from 'utils/Toast';
import { ClaimRejectModal } from 'containers/claims';
import { SystemColors } from 'melp-design/style';
import { useRouteParams } from 'utils/useIdParam';
import { Redirect } from 'react-router-dom';
import { EmployeeCell } from 'components/table';

interface Values {
  purchaseDate: string;
  approvedAmount: number;
  supplier: string;
}

const INITIAL_VALUES: Values = {
  purchaseDate: '',
  approvedAmount: 0,
  supplier: '',
};

export const ClaimDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: claimId } = useRouteParams(ROUTES.claims.details);
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { hasViewPermission, hasEditPermission } = useMe();
  const { currency } = useDefaultCurrency();

  const { data: claim, isLoading } = useClaim(claimId);
  const { mutate: updateClaim, isLoading: isUpdatingClaim } = useUpdateClaim();

  const { control, reset, getValues, watch } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (claim) {
      reset({
        purchaseDate: claim.purchasedAt,
        approvedAmount: fromCents(claim.approvedAmount),
        supplier: claim.supplier,
      });
    }
  }, [claim, reset]);

  if (!hasViewPermission(['employees'])) {
    return <Redirect to={ROUTES.root.notFound} />;
  }

  if (isLoading) return <Loader />;
  if (!claim) return <NothingFoundAlert />;

  const [approvedAmount] = watch(['approvedAmount']);
  const isApprovedAmountValid =
    approvedAmount <= fromCents(claim.requestedAmount);

  const FOOTER_BY_STATUS: Record<ClaimStatus, ReactNode> = {
    approved: null,
    paid: null,
    rejected: null,
    pendingEmployeeAction: (
      <Footer>
        <Stack direction="row" justifyContent="end" gap="20px">
          <Button
            label={t('global.actions.reject')}
            variant="danger-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'reject' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  data: {
                    status: response.payload.status,
                    adminComment: response.payload.comment,
                  },
                });
              }
            }}
            disabled={
              isUpdatingClaim || !hasEditPermission(['expenseCompensations'])
            }
          />
          <Button
            label={t('global.actions.approve_amount', {
              amount: formatCurrency(
                i18n.language,
                toCents(approvedAmount),
                currency,
              ),
            })}
            variant="secondary"
            onClick={() => {
              const values = getValues();
              updateClaim(
                {
                  claimId: claim.id,
                  data: {
                    status: 'approved',
                    purchaseDate: values.purchaseDate,
                    approvedAmount: toCents(values.approvedAmount),
                    supplier: values.supplier,
                  },
                },
                { onSuccess: () => predefinedToasts.success.updated() },
              );
            }}
            disabled={
              isUpdatingClaim ||
              !hasEditPermission(['expenseCompensations']) ||
              !approvedAmount ||
              !isApprovedAmountValid
            }
          />
        </Stack>
      </Footer>
    ),
    pendingHRAction: (
      <Footer>
        <Stack direction="row" justifyContent="end" gap="20px">
          <Button
            label={t('claims.actions.request_extra_input')}
            variant="neutral-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'request' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  data: {
                    status: response.payload.status,
                    adminComment: response.payload.comment,
                  },
                });
              }
            }}
            disabled={
              isUpdatingClaim || !hasEditPermission(['expenseCompensations'])
            }
          />

          <Button
            label={t('global.actions.reject')}
            variant="danger-outline"
            onClick={async () => {
              const response = await openModal({
                component: ClaimRejectModal,
                props: { title: claim.supplier, action: 'reject' },
              });

              if (response.action === 'REJECT') {
                updateClaim({
                  claimId: claim.id,
                  data: {
                    status: response.payload.status,
                    adminComment: response.payload.comment,
                  },
                });
              }
            }}
            disabled={
              isUpdatingClaim || !hasEditPermission(['expenseCompensations'])
            }
          />

          <Button
            label={t('global.actions.approve_amount', {
              amount: formatCurrency(
                i18n.language,
                toCents(approvedAmount),
                currency,
              ),
            })}
            variant="secondary"
            onClick={() => {
              const values = getValues();
              updateClaim({
                claimId: claim.id,
                data: {
                  status: 'approved',
                  purchaseDate: values.purchaseDate,
                  approvedAmount: toCents(values.approvedAmount),
                  supplier: values.supplier,
                },
              });
            }}
            disabled={
              isUpdatingClaim ||
              !hasEditPermission(['expenseCompensations']) ||
              !approvedAmount ||
              !isApprovedAmountValid
            }
          />
        </Stack>
      </Footer>
    ),
  };

  const DETAILS_ITEMS: Array<{ label: string; value: ReactNode }> = [
    {
      label: t('common.full_name'),
      value: <EmployeeCell employee={claim.employee} />,
    },
    {
      label: t('wallet.employeeWalletFundsSource'),
      value: (
        <List sx={{ py: 0 }}>
          {claim.sources.map((s) => (
            <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
              {s.id === RECOGNITION_SHOP_ID ? (
                hasViewPermission(['employeeRecognition']) ? (
                  <Link label={s.name} to={ROUTES.recognitions.settings} />
                ) : (
                  <Typography>{s.name}</Typography>
                )
              ) : s.id === COMPANY_EXPENSE_ID ? (
                <Link label={s.name} to={ROUTES.claims.settings} />
              ) : hasViewPermission(['benefits']) ? (
                <Link
                  label={s.name}
                  to={ROUTES.benefits.details.replace(':id', s.id)}
                />
              ) : (
                <Typography>{s.name}</Typography>
              )}
            </ListItem>
          ))}
        </List>
      ),
    },
    {
      label: t('claims.table.requested_amount.title'),
      value: (
        <Typography>
          {formatCurrency(i18n.language, claim.requestedAmount, currency)}
        </Typography>
      ),
    },
    {
      label: t('common.status'),
      value: (
        <>
          <Typography
            color={COLOR_BY_STATUS[claim.status]}
            variant="p1"
            style={{ fontWeight: 500 }}
          >
            {t(`claims.status.${claim.status}`)}
          </Typography>
          {['pendingEmployeeAction', 'approved', 'rejected'].includes(
            claim.status,
          ) ? (
            <>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {claim.admin.fullName}
              </Typography>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {claim.admin.email}
              </Typography>
              <Typography color={COLOR_BY_STATUS[claim.status]}>
                {moment(claim.updatedAt).format('l LT')}
              </Typography>
            </>
          ) : null}
        </>
      ),
    },
    ...(claim.description
      ? [
          {
            label: t('employees.details'),
            value: <Typography>{claim.description}</Typography>,
          },
        ]
      : []),
    ...(claim.adminComment
      ? [
          {
            label: t('clients.comment'),
            value: <Typography>{claim.adminComment}</Typography>,
          },
        ]
      : []),
    {
      label: t('marketplaceItems.supplier'),
      value: (
        <Controller
          name="supplier"
          control={control}
          render={({ field: { name, value, onChange, ref } }) => (
            <TextInput
              name={name}
              value={value}
              onChange={onChange}
              ref={ref}
            />
          )}
        />
      ),
    },
    {
      label: t('claims.table.purchase_date.title'),
      value: (
        <Controller
          name="purchaseDate"
          control={control}
          render={({ field: { name, value, onChange, ref } }) => (
            <DateInput
              name={name}
              value={value}
              onChange={onChange}
              ref={ref}
            />
          )}
        />
      ),
    },
    {
      label: t('claims.table.approved_amount.title'),
      value: (
        <Controller
          name="approvedAmount"
          control={control}
          rules={{
            validate: (value) =>
              value <= fromCents(claim.requestedAmount) ||
              `${t('claims.table.requested_amount.title')}: ${formatCurrency(
                i18n.language,
                claim.requestedAmount,
                currency,
              )}`,
          }}
          render={({ field: { name, value, onChange, ref }, fieldState }) => (
            <NumberInput
              key={value}
              name={name}
              value={value}
              onChange={onChange}
              ref={ref}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      ),
    },
  ];

  const claimFiles = {
    images: claim.files.filter((file) => file.mime.startsWith('image')),
    docs: claim.files.filter((file) => !file.mime.startsWith('image')),
  };

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('claims.title'),
            url: ROUTES.claims.list,
          },
          {
            label: t('claims.details.title.full', {
              interpolation: { escapeValue: false },
              date: moment(claim.claimedAt).format('l LT'),
              user: claim.employee?.fullName,
            }),
          },
        ]}
        under={
          <Typography color={COLOR_BY_STATUS[claim.status]}>
            {t(`claims.status.${claim.status}`)}
          </Typography>
        }
      />

      <Stack gap={2} flexDirection="row">
        <Box width="50%">
          <Panel>
            {claimFiles.docs.map((file) => (
              <Box key={file.id} py={0.5}>
                <ExternalLink label={file.name} href={file.url} />
              </Box>
            ))}
            {claimFiles.images.length ? (
              <ImageList cols={1}>
                {claimFiles.images.map((file) => (
                  <ImageListItem key={file.id}>
                    <ImageZoom src={file.url} alt={file.name} />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : null}
          </Panel>
        </Box>

        <Box width="50%">
          <Stack gap={2}>
            <Panel>
              <Stack spacing={2}>
                {DETAILS_ITEMS.map((item, i) => (
                  <Stack gap={2} flexDirection="row" key={i}>
                    <Box width="40%">
                      <Typography color="textSecondary">
                        {item.label}
                      </Typography>
                    </Box>
                    <Box width="60%">{item.value}</Box>
                  </Stack>
                ))}
              </Stack>
            </Panel>

            {claim.taxExemptionAnalysis ? (
              <Panel
                title={t('claims.taxExemptionAnalysis.title')}
                side={
                  <Chip
                    variant={
                      CHIP_VARIANT_BY_TAX_EXEMPTION_ANALYSIS_STATUS[
                        claim.taxExemptionAnalysis.status
                      ]
                    }
                    label={t(`status.${claim.taxExemptionAnalysis.status}`)}
                  />
                }
              >
                <Stack spacing={2}>
                  <LabeledValue
                    label={t('benefitExpenses.summary')}
                    value={claim.taxExemptionAnalysis.summary}
                  />

                  {claim.taxExemptionAnalysis.notes.length ? (
                    <Alert
                      severity="warning"
                      title={t('claims.taxExemptionAnalysis.notes.label')}
                    >
                      {claim.taxExemptionAnalysis.notes.join(' ')}
                    </Alert>
                  ) : null}

                  <Typography variant="h2">
                    {t('claims.taxExemptionAnalysis.items.label')}
                  </Typography>

                  <List sx={{ py: 0 }}>
                    {claim.taxExemptionAnalysis.items.map((item, i) => (
                      <ListItem sx={{ p: 0, mb: 2 }} key={i}>
                        <Stack
                          borderRadius={1}
                          boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                          width="100%"
                          alignItems="flex-start"
                          p={2}
                          gap={2}
                        >
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            width="100%"
                            gap={5}
                          >
                            <Typography>{item.title}</Typography>
                            <Typography variant="h3">
                              {formatCurrency(
                                i18n.language,
                                item.amount,
                                currency,
                              )}
                            </Typography>
                          </Stack>

                          <Chip
                            variant={
                              CHIP_VARIANT_BY_TAX_EXEMPTION_ANALYSIS_STATUS[
                                item.status
                              ]
                            }
                            label={t(`status.${item.status}`)}
                          />

                          <ul>
                            {item.reasons.map((reason, i) => (
                              <li key={i}>{reason}</li>
                            ))}
                          </ul>
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              </Panel>
            ) : null}
          </Stack>
        </Box>
      </Stack>

      {FOOTER_BY_STATUS[claim.status]}
    </Stack>
  );
};
